import CircularProgress from "@mui/material/CircularProgress";
import { useModuleListByTagIdQuery } from "entities/modules/queries/useModuleListByTagIdQuery";
import { Tag } from "entities/tags/types";
import { useNavigate } from "react-router-dom";
import DeleteConfirmationModal from "shared/components/DeleteConfirmationModal";
import FlexCenter from "shared/components/Layout/components/FlexCenter";
import { DrawerHookResult } from "shared/hooks/useDrawerState";
import { MODULES_PATH } from "shared/routes/paths";
import { COLOR_ERROR, COLOR_PRIMARY_MAIN } from "shared/theme/colors";
import { generateTextForDeletingPopup } from "./helpers";

type DeleteTagModalProps<T extends Tag> = {
  tagsDetailsDrawer: DrawerHookResult<T>;
  setOpenModal: () => void;
  openModal: boolean;
  loadingDelete: boolean;
  selectedTag: T | null;
  onDeleteTag: () => Promise<void>;
};

const DeleteTagModal = <T extends Tag>({
  tagsDetailsDrawer,
  setOpenModal,
  openModal,
  loadingDelete,
  selectedTag,
  onDeleteTag,
}: DeleteTagModalProps<T>) => {
  const navigate = useNavigate();
  const { data: moduleList = [], isFetching: isLoadingModuleList } =
    useModuleListByTagIdQuery(selectedTag?.id);

  const moduleListWithSingleTagOnly = moduleList.filter(
    (module) =>
      module.tags.length === 1 && module.tags.includes(selectedTag?.id ?? ""),
  );

  const closeModal = () => {
    tagsDetailsDrawer.setDrawerDetails(undefined);
    setOpenModal();
  };

  const selectedGamesCount = selectedTag?.matchingGamesCount || 0;
  const allModulesAmount = moduleList?.length || 0;
  const singleTagModulesAmount = moduleListWithSingleTagOnly.length;
  const moduleListNames =
    moduleListWithSingleTagOnly?.map((module) => module.name) ?? [];

  const hasSingleTagModules = Boolean(singleTagModulesAmount);

  const primaryButtonTitle = hasSingleTagModules
    ? "Go to Modules"
    : "Yes, delete";

  const redirectionModulePath = moduleListWithSingleTagOnly.find(
    (module) => module?.redirectionPath,
  )?.redirectionPath;

  const handleSuccess = hasSingleTagModules
    ? () => navigate(redirectionModulePath || MODULES_PATH)
    : onDeleteTag;

  return (
    <DeleteConfirmationModal
      title="Delete tag"
      content={
        !isLoadingModuleList
          ? generateTextForDeletingPopup({
              matchingGamesCount: selectedGamesCount,
              moduleNames: moduleListNames,
              modulesCount: allModulesAmount,
              hasSingleTagModules,
            })
          : ""
      }
      applyButtonTitle={primaryButtonTitle}
      onClose={closeModal}
      open={openModal}
      handleSuccess={handleSuccess}
      applyButtonLoading={loadingDelete || isLoadingModuleList}
      primaryButtonColor={
        hasSingleTagModules ? COLOR_PRIMARY_MAIN : COLOR_ERROR
      }
    >
      {isLoadingModuleList ? (
        <FlexCenter margin={"0 auto"}>
          <CircularProgress />
        </FlexCenter>
      ) : null}
    </DeleteConfirmationModal>
  );
};

export default DeleteTagModal;
